import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useAuth } from 'hooks';
import { useDefaultCategories } from 'hooks/useDefaultSearchCategories';
import {
  aiOrganisationFilters,
  aiOrganisationRecentSearches,
} from 'services/api';
import { getPendingAiSessionId } from 'selectors/searchNLP';
import { fullResetFilters, applyAiFilters } from 'actions/organisation';
import { view } from 'components/Search/Results/constants';
import { ORGANISATIONPEOPLEVIEW, ORGANISATIONWORKITEMVIEW } from './helpers';
import { NlpOrgContext } from 'components/SearchNLP/NlpOrgContext';
import { NLPSearchModal } from 'components/SearchNLP/NLPSearchModal';
import { NLPFeedbackModal } from 'components/SearchNLP/NLPFeedbackModal';
import { mapAiFiltersToOrgSlice } from './aiFilterAllowlist';
import { openMainSearchWithQuery } from 'components/SearchNLP/orgRedirectContract';

// SCIL-17603/17604/17605: organisation-profile entry point for SciSearch AI.
// Renders the SAME modal main search uses (via NlpOrgContext, no fork), applies
// accepted filters to the shared People/Activity slice, and offers to re-run
// queries main search can serve in a new tab. The open buttons live in the
// People/Activity search bars (mirroring main search); they open this modal via
// the shared setShowNLPFilterModal redux flag.

// Only this rejection earns "Run on main search" in the modal; every other rejection
// is a dead end. An accepted query that matches nothing offers it too, from the
// results empty state (OrgSciAiEmptyState).
const MAIN_SEARCH_REDIRECT_REASON = 'redirect:main-search';

/** True when the org endpoint says the query belongs on main search. */
const isMainSearchRedirectRejection = response => {
  if (!response) return false;
  const reason = response.rejectionReason ?? response.RejectionReason ?? '';
  return (
    response.errorCode === 'AISearch.QueryRejected' &&
    String(reason).toLowerCase() === MAIN_SEARCH_REDIRECT_REASON
  );
};

// The AI classifies a query as a researcher or activity search; only those two
// views map onto a profile tab. Anything else stays on the tab the user is on.
const tabForSearchView = (searchView, activeTab) => {
  if (searchView === view.activity) return ORGANISATIONWORKITEMVIEW;
  if (searchView === view.researcher) return ORGANISATIONPEOPLEVIEW;
  return activeTab;
};

/**
 * @param {Object} props
 * @param {string} props.organisationId
 * @param {string} props.organisationName
 * @param {string} props.activeTab active org view constant
 * @param {(tab: string) => void} props.onRunSearchOnTab switches to the tab and runs its search
 */
export const SciAIOrgSearch = ({
  organisationId,
  organisationName,
  activeTab,
  onRunSearchOnTab,
}) => {
  const { user } = useAuth();
  const defaultCategories = useDefaultCategories();
  const dispatch = useDispatch();
  const pendingAiSessionId = useSelector(getPendingAiSessionId);

  // The tab follows the AI's classification, mirroring main search's view switch.
  const runSearchOn = useCallback(
    searchView => onRunSearchOnTab(tabForSearchView(searchView, activeTab)),
    [activeTab, onRunSearchOnTab]
  );

  const applyOrgFilters = useCallback(
    enrichedFilters => {
      const { filters } = mapAiFiltersToOrgSlice(enrichedFilters);
      // No AI category means "search across everything", so seed the same
      // selection a fresh page starts with rather than leave every box unticked.
      const seeded = {
        workItemCategories: { ...defaultCategories },
        ...filters,
      };
      // Atomic: clear the slice, then merge the mapped filters + session id in one
      // action so the slice is never half-populated. The search is run separately
      // by the caller AFTER the session is activated (see NlpOrgContext), so the
      // baseline middleware never tears down the just-created session.
      dispatch(fullResetFilters(organisationId));
      dispatch(applyAiFilters(organisationId, seeded, pendingAiSessionId));
    },
    [dispatch, organisationId, pendingAiSessionId, defaultCategories]
  );

  // API EntityType for the active tab: People -> 1 (Researcher), Activity -> 3.
  // Never 2 (Organisation) — the org endpoint rejects it (you can't search for
  // organisations from an organisation's own profile).
  const entityType = activeTab === ORGANISATIONWORKITEMVIEW ? 3 : 1;

  const contextValue = useMemo(
    () => ({
      organisationId,
      organisationName,
      entityType,
      filtersUrl: aiOrganisationFilters(organisationId),
      recentSearchesUrl: aiOrganisationRecentSearches(organisationId),
      applyOrgFilters,
      runSearchOn,
      isMainSearchRedirectRejection,
      onMainSearchRedirect: openMainSearchWithQuery,
    }),
    [organisationId, organisationName, entityType, applyOrgFilters, runSearchOn]
  );

  if (!user?.hasSciSearchAI) {
    return null;
  }

  return (
    <NlpOrgContext.Provider value={contextValue}>
      <NLPSearchModal />
      {/* SCIL-17609: inside the provider so submissions carry the org id/name as their source. */}
      <NLPFeedbackModal />
    </NlpOrgContext.Provider>
  );
};
